import { useCallback, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  companyIdSelector,
  createSuccessResult,
  dropdownPassengersWithStationsAtom,
  errors,
  IProfileDto,
  isLoggedInAtom,
  isRideEntity,
  Log,
  ProfileApi,
  profileDtoAtom,
  profileScopeAtom,
  RequestBodyModel,
  RydeUiUnit,
  SimpleResult,
  simpleResultAuthenticationError,
  simpleResultErrorEmptyResponse,
  simpleResultFromException,
  SystemSettingsApi,
  systemSettingsDtoAtom,
  useHasPermissions,
} from "@ryde/shared";

import {
  useRideBookingActions,
  useRidesJourneyActions,
  useTrackingActions,
} from "../rides";
import { useLoggedInRef } from "./use-logged-in-ref";

export const useProfileActions = () => {
  const setProfileDto = useSetRecoilState(profileDtoAtom);
  const setProfileScope = useSetRecoilState(profileScopeAtom);
  // TODO refactor, used same atom
  const setProfileIsFetching = useSetRecoilState(apiDataFetchingAtom);
  const setIsLoggedIn = useSetRecoilState(isLoggedInAtom);
  const isLoggedInRef = useLoggedInRef();
  const setSystemSettingsData = useSetRecoilState(systemSettingsDtoAtom);
  // TODO refactor, used same atom
  const setSystemSettingsIsFetching = useSetRecoilState(apiDataFetchingAtom);
  const setPassengersWithStations = useSetRecoilState(
    dropdownPassengersWithStationsAtom,
  );
  const companyId = useRecoilValue(companyIdSelector);
  const { fetchTrackingForEditingRide } = useTrackingActions();
  const { fetchBookingsForEditingRide } = useRideBookingActions();
  const { fetchRidesForEditingJourney } = useRidesJourneyActions();
  const { hasPermissions: hasPermissionFetchTracking } = useHasPermissions({
    entity: "tracking",
    action: "read",
  });
  const { hasPermissions: hasPermissionFetchBookings } = useHasPermissions({
    entity: "booking",
    action: "read",
  });
  const { hasPermissions: hasPermissionFetchRides } = useHasPermissions({
    entity: "ride",
    action: "read",
  });

  const [, startTransition] = useTransition();
  const { t } = useTranslation();

  const fetchProfile = useEvent(
    async (): Promise<SimpleResult<IProfileDto>> => {
      Log.debug(`Fetch profile called `);

      try {
        if (!isLoggedInRef.current) {
          return simpleResultAuthenticationError("User not logged in");
        }

        setProfileIsFetching(true);
        const dto = await ProfileApi.getProfile();

        if (!isLoggedInRef.current) {
          return simpleResultAuthenticationError("User not logged in");
        }

        if (!dto) {
          return simpleResultErrorEmptyResponse(t);
        }

        Log.debug("Fetching profile, set to: ", dto);
        setProfileDto(dto);
        return createSuccessResult(dto);
      } catch (error) {
        Log.warn("Error fetching profile", error);
        return simpleResultFromException({
          error,
          fallbackTranslationKey: errors.auth.failedFetchProfile,
        });
      } finally {
        setProfileIsFetching(false);
      }
    },
  );

  const updateProfile = useEvent(
    async (
      payload: Partial<IProfileDto>,
    ): Promise<SimpleResult<IProfileDto>> => {
      Log.debug(`Update profile called `);

      try {
        setProfileIsFetching(true);
        const dto = await ProfileApi.update(payload);

        if (!dto) {
          return simpleResultErrorEmptyResponse(t);
        }

        Log.debug("Updated profile received: ", dto);
        setProfileDto(dto);
        return createSuccessResult(dto);
      } catch (error) {
        Log.warn("Error updating profile", error);
        return simpleResultFromException({
          error,
          fallbackTranslationKey: errors.auth.failedUpdateProfile,
        });
      } finally {
        setProfileIsFetching(false);
      }
    },
  );

  const clearProfileRecoilStore = useEvent((): void => {
    Log.debug("Clear profile recoil store called");
    setIsLoggedIn(false);
    setProfileScope(undefined);
    setProfileDto(undefined);
    setProfileIsFetching(false);
  });

  const fetchSystemSettings = useEvent(
    async (currentCompanyId: number): Promise<SimpleResult<void>> => {
      Log.debug(`Fetch system Settings called `);

      try {
        if (!isLoggedInRef.current) {
          return simpleResultAuthenticationError("User not logged in");
        }

        setSystemSettingsIsFetching(true);
        const dto = await SystemSettingsApi.getSystemSettings({
          companyId: currentCompanyId,
        });

        if (!isLoggedInRef.current) {
          return simpleResultAuthenticationError("User not logged in");
        }

        if (!dto) {
          return simpleResultErrorEmptyResponse(t);
        }

        Log.debug("System settings received for: ", dto);
        setSystemSettingsData(dto);
        return createSuccessResult();
      } catch (error) {
        Log.warn("System settings fetching failed", error);
        return simpleResultFromException({
          error,
          fallbackTranslationKey: errors.common.failedFetchSystemSettings,
        });
      } finally {
        setSystemSettingsIsFetching(false);
      }
    },
  );

  const afterSaveCallback = useCallback(
    (entity: RydeUiUnit): void => {
      if (entity === "systemParam" && companyId) {
        startTransition(() => {
          fetchSystemSettings(companyId);
        });
      } else if (entity === "passenger") {
        setPassengersWithStations(undefined);
      }
    },
    [companyId, fetchSystemSettings, setPassengersWithStations],
  );

  const afterFetchCrudDtoCallback = useCallback(
    <T extends RequestBodyModel>({
      entity,
      dtoFromApi,
    }: {
      entity: RydeUiUnit;
      dtoFromApi: T;
    }): void => {
      if (isRideEntity(entity) && dtoFromApi?.id) {
        startTransition(() => {
          if (hasPermissionFetchTracking) {
            fetchTrackingForEditingRide(dtoFromApi.id);
          }
          if (hasPermissionFetchBookings) {
            fetchBookingsForEditingRide(dtoFromApi.id);
          }
        });
      } else if (entity === "journey" && dtoFromApi?.id) {
        startTransition(() => {
          if (hasPermissionFetchRides) {
            fetchRidesForEditingJourney(dtoFromApi.id, dtoFromApi?.rideIds);
          }
        });
      }
    },
    [
      fetchTrackingForEditingRide,
      fetchBookingsForEditingRide,
      fetchRidesForEditingJourney,
      hasPermissionFetchTracking,
      hasPermissionFetchBookings,
      hasPermissionFetchRides,
    ],
  );

  return {
    fetchProfile,
    updateProfile,
    clearProfileRecoilStore,
    fetchSystemSettings,
    afterSaveCallback,
    afterFetchCrudDtoCallback,
  };
};
